import type {
  Content,
  ContentHref,
  ContentRelation,
  ContentRelationHref,
} from '@generalTypes/apiTypes';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type {
  DocumentSnapshotState,
  PersistKrcSnapshotPayload,
  Snapshot,
} from './documentSnapshotTypes';

const getInitialState = (): DocumentSnapshotState => ({
  snapshots: [],
  snapshotsFetched: false,
  snapshotChangelog: '',
  snapshotChangelogStatus: 'idle',
  selectedSnapshotVersion: null,
  snapshotContent: {},
  snapshotRelations: {},
  isSnapshotContentFetched: false,
  isSnapshotContentFetchFailed: false,
  isPersistingSnapshot: false,
});

const documentSnapshotSlice = createSlice({
  name: 'documentSnapshot',
  initialState: getInitialState,
  reducers: {
    fetchSnapshots(): void {},
    snapshotsFetched(state, action: PayloadAction<{ snapshots: Snapshot[] }>) {
      state.snapshots = action.payload.snapshots;
      state.snapshotsFetched = true;
    },
    prepareSnapshotChangelog(state) {
      state.snapshotChangelog = '';
      state.snapshotChangelogStatus = 'loading';
    },
    snapshotChangelogPrepared(state, action: PayloadAction<{ changelog: string }>) {
      state.snapshotChangelog = action.payload.changelog;
      state.snapshotChangelogStatus = 'succeeded';
    },
    snapshotChangelogPreparationFailed(state) {
      state.snapshotChangelogStatus = 'failed';
    },
    selectSnapshot(state, action: PayloadAction<{ version: string | null }>) {
      state.selectedSnapshotVersion = action.payload.version;
      // Always clear previous snapshot data and reset flags so the loading
      // spinner shows correctly on every selection change, not just the first.
      state.snapshotContent = {};
      state.snapshotRelations = {};
      state.isSnapshotContentFetched = false;
      state.isSnapshotContentFetchFailed = false;
    },
    snapshotContentFetchFailed(state) {
      state.isSnapshotContentFetchFailed = true;
    },
    snapshotContentFetched(
      state,
      action: PayloadAction<{ content: Content[]; relations: ContentRelation[] }>,
    ) {
      const contentMap: Record<ContentHref, Content> = {};
      action.payload.content.forEach((item) => {
        contentMap[item.$$meta.permalink] = item;
      });
      const relationsMap: Record<ContentRelationHref, ContentRelation> = {};
      action.payload.relations.forEach((rel) => {
        relationsMap[rel.$$meta.permalink] = rel;
      });
      state.snapshotContent = contentMap;
      state.snapshotRelations = relationsMap;
      state.isSnapshotContentFetched = true;
    },
    clearSnapshot(state) {
      state.selectedSnapshotVersion = null;
      state.snapshotContent = {};
      state.snapshotRelations = {};
      state.isSnapshotContentFetched = false;
      state.isSnapshotContentFetchFailed = false;
      state.snapshots = [];
      state.snapshotsFetched = false;
      state.snapshotChangelog = '';
      state.snapshotChangelogStatus = 'idle';
    },
    persistKrcSnapshot: (state, _action: PayloadAction<PersistKrcSnapshotPayload>) => {
      // the actual persist work is done in the saga; flag drives the button spinner
      state.isPersistingSnapshot = true;
    },
    krcSnapshotPersistFinished: (state) => {
      state.isPersistingSnapshot = false;
    },
  },
});

export const {
  fetchSnapshots,
  snapshotsFetched,
  prepareSnapshotChangelog,
  snapshotChangelogPrepared,
  snapshotChangelogPreparationFailed,
  selectSnapshot,
  snapshotContentFetched,
  snapshotContentFetchFailed,
  clearSnapshot,
  persistKrcSnapshot,
  krcSnapshotPersistFinished,
} = documentSnapshotSlice.actions;

export default documentSnapshotSlice.reducer;
